import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Calendar, Check, ChevronLeft, Users, Sparkles, AlertCircle, Loader2, MapPin, Phone, Mail, User as UserIcon } from 'lucide-react';
import { WellnessNav, WellnessFooter } from './WellnessLanding';
import { WELLNESS_API, fmtUSD, today, addDays, nightsBetween } from './_shared';

export default function WellnessBooking() {
  const [params] = useSearchParams();
  const nav = useNavigate();

  const [rooms, setRooms] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [step, setStep] = useState(1);  // 1=stay, 2=treatments, 3=guest, 4=review
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [checkingAvail, setCheckingAvail] = useState(false);

  // Form state
  const [roomId, setRoomId] = useState(params.get('room') || '');
  const [checkIn, setCheckIn] = useState(addDays(today(), 14));
  const [checkOut, setCheckOut] = useState(addDays(today(), 28));
  const [guests, setGuests] = useState(1);
  const [treatmentIds, setTreatmentIds] = useState([]);
  const [guest, setGuest] = useState({
    full_name: '', email: '', phone: '', country: '', age: '',
    gender: '', medical_history: '', current_ailments: '',
    allergies: '', current_medications: '',
  });
  const [consultation, setConsultation] = useState({ requested: false, preferred_date: '', notes: '' });
  const [notes, setNotes] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [r, t] = await Promise.all([
          axios.get(`${WELLNESS_API}/rooms`),
          axios.get(`${WELLNESS_API}/treatments`),
        ]);
        setRooms(r.data || []);
        setTreatments(t.data || []);
        if (!roomId && r.data?.length) setRoomId(r.data[0].id);
      } catch { /* noop */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedRoom = rooms.find((r) => r.id === roomId);
  const nights = nightsBetween(checkIn, checkOut);
  const roomTotal = selectedRoom ? selectedRoom.price_per_night * nights : 0;
  const selectedTreatments = treatments.filter((t) => treatmentIds.includes(t.id));
  const treatmentsTotal = selectedTreatments.reduce((sum, t) => sum + Math.min(nights, t.duration_days) * t.price_per_day, 0);
  const grandTotal = roomTotal + treatmentsTotal;

  // Availability check whenever stay changes
  const checkAvailability = useCallback(async (rid, ci, co, n) => {
    if (!rid || !ci || !co || n < 7 || n > 21) {
      setAvailability(null);
      return;
    }
    setCheckingAvail(true);
    setAvailability(null);
    try {
      const r = await axios.get(`${WELLNESS_API}/availability`, {
        params: { room_id: rid, check_in: ci, check_out: co },
      });
      setAvailability(r.data);
    } catch {
      setAvailability({ available: false, error: 'Could not check availability' });
    } finally {
      setCheckingAvail(false);
    }
  }, []);

  // Trigger initial check + refresh on changes (debounced via setTimeout in handlers below)
  useEffect(() => {
    const t = setTimeout(() => { checkAvailability(roomId, checkIn, checkOut, nights); }, 250);
    return () => clearTimeout(t);
  }, [roomId, checkIn, checkOut, nights, checkAvailability]);

  const canProceedStay = selectedRoom && nights >= 7 && nights <= 21 && availability?.available;
  const canProceedGuest = guest.full_name.trim().length >= 2 && /\S+@\S+\.\S+/.test(guest.email) && guest.phone.trim().length >= 6 && guest.country.trim().length >= 2 && Number(guest.age) >= 10;

  async function submitBooking() {
    if (submitting) return;
    setSubmitting(true);
    try {
      const body = {
        room_id: roomId,
        check_in: checkIn,
        check_out: checkOut,
        guests: Number(guests),
        treatment_ids: treatmentIds,
        guest: { ...guest, age: Number(guest.age) },
        consultation,
        notes,
      };
      const r = await axios.post(`${WELLNESS_API}/bookings`, body);
      toast.success('Booking confirmed!');
      nav(`/wellness/confirmation/${r.data.confirmation_code}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Booking failed. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#faf7ef] text-[#2a2620]" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }} data-testid="wellness-booking-page">
      <Toaster richColors position="top-right" />
      <WellnessNav />

      <div className="max-w-5xl mx-auto px-6 py-10">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Book your wellness stay</h1>
        <p className="text-sm text-[#6b6358] mb-6 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
          Pay on arrival · Free cancellation up to 7 days before check-in
        </p>

        <Steps step={step} />

        <div className="grid lg:grid-cols-[1fr,360px] gap-8 mt-6">
          {/* MAIN PANEL */}
          <div className="bg-white rounded-2xl border border-[#e0d6c2] shadow-sm p-6 sm:p-8">
            {step === 1 && (
              <StepStay
                rooms={rooms} roomId={roomId} setRoomId={setRoomId}
                checkIn={checkIn} setCheckIn={setCheckIn}
                checkOut={checkOut} setCheckOut={setCheckOut}
                guests={guests} setGuests={setGuests}
                nights={nights} availability={availability} checking={checkingAvail}
                selectedRoom={selectedRoom}
              />
            )}
            {step === 2 && (
              <StepTreatments
                treatments={treatments} treatmentIds={treatmentIds}
                setTreatmentIds={setTreatmentIds} nights={nights}
              />
            )}
            {step === 3 && (
              <StepGuest
                guest={guest} setGuest={setGuest}
                consultation={consultation} setConsultation={setConsultation}
                checkIn={checkIn} notes={notes} setNotes={setNotes}
              />
            )}
            {step === 4 && (
              <StepReview
                room={selectedRoom} nights={nights} checkIn={checkIn} checkOut={checkOut}
                guests={guests} treatments={selectedTreatments}
                roomTotal={roomTotal} treatmentsTotal={treatmentsTotal} grandTotal={grandTotal}
                guest={guest} consultation={consultation} notes={notes}
              />
            )}

            {/* Step navigation */}
            <div className="mt-8 flex items-center justify-between gap-3 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              <button
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={step === 1}
                className="px-5 py-2.5 text-sm font-semibold text-[#2f5d50] hover:bg-[#f3ece0] rounded-full transition disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1"
                data-testid="step-back"
              >
                <ChevronLeft className="w-4 h-4" />Back
              </button>
              {step < 4 ? (
                <button
                  onClick={() => setStep((s) => Math.min(4, s + 1))}
                  disabled={(step === 1 && !canProceedStay) || (step === 3 && !canProceedGuest)}
                  className="px-7 py-3 rounded-full bg-[#2f5d50] hover:bg-[#244a40] text-white text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="step-next"
                >
                  {step === 1 ? 'Continue to treatments' : step === 2 ? 'Continue to your details' : 'Review booking'}
                </button>
              ) : (
                <button
                  onClick={submitBooking}
                  disabled={submitting}
                  className="px-7 py-3 rounded-full bg-[#c89b3c] hover:bg-[#b58931] text-white text-sm font-semibold transition disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="confirm-booking"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Confirm Booking
                </button>
              )}
            </div>
          </div>

          {/* SIDEBAR SUMMARY */}
          <SummarySidebar
            room={selectedRoom} nights={nights} checkIn={checkIn} checkOut={checkOut}
            treatments={selectedTreatments} roomTotal={roomTotal}
            treatmentsTotal={treatmentsTotal} grandTotal={grandTotal}
            guests={guests} availability={availability}
          />
        </div>
      </div>

      <WellnessFooter />
    </div>
  );
}

// ---------- Step components ----------

function Steps({ step }) {
  const labels = ['Stay', 'Treatments', 'Your details', 'Review'];
  return (
    <div className="flex items-center gap-2 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
      {labels.map((l, i) => {
        const n = i + 1;
        const active = step === n;
        const done = step > n;
        return (
          <React.Fragment key={l}>
            <div className={`flex items-center gap-2 text-xs font-semibold ${active ? 'text-[#2f5d50]' : done ? 'text-[#c89b3c]' : 'text-[#b8ad9a]'}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${active ? 'bg-[#2f5d50] text-white' : done ? 'bg-[#c89b3c] text-white' : 'bg-[#e0d6c2] text-[#6b6358]'}`}>
                {done ? <Check className="w-3.5 h-3.5" /> : n}
              </span>
              <span className="hidden sm:inline">{l}</span>
            </div>
            {i < labels.length - 1 && <div className="flex-1 h-px bg-[#e0d6c2]" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StepStay({ rooms, roomId, setRoomId, checkIn, setCheckIn, checkOut, setCheckOut, guests, setGuests, nights, availability, checking, selectedRoom }) {
  const validNights = nights >= 7 && nights <= 21;
  return (
    <div className="font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
      <h2 className="text-xl font-bold mb-1 text-[#2a2620]" style={{ fontFamily: 'Georgia, serif' }}>Choose your stay</h2>
      <p className="text-xs text-[#6b6358] mb-5">Minimum 7 nights · Maximum 21 nights</p>

      <Label>Room category</Label>
      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        {rooms.map((r) => {
          const active = roomId === r.id;
          return (
            <button
              key={r.id}
              onClick={() => setRoomId(r.id)}
              className={`text-left p-3 rounded-xl border-2 transition ${active ? 'border-[#2f5d50] bg-[#f3ece0]' : 'border-[#e0d6c2] bg-white hover:border-[#c89b3c]'}`}
              data-testid={`pick-room-${r.id}`}
            >
              <p className="text-sm font-bold">{r.name}</p>
              <p className="text-[10px] text-[#6b6358] mt-0.5">{r.subtitle}</p>
              <p className="text-sm font-bold text-[#2f5d50] mt-2">{fmtUSD(r.price_per_night)}<span className="text-[10px] text-[#6b6358] font-normal">/night</span></p>
            </button>
          );
        })}
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <div>
          <Label>Check-in</Label>
          <input
            type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)}
            min={today()}
            className="w-full h-11 px-3 rounded-xl border border-[#e0d6c2] text-sm focus:border-[#2f5d50] focus:outline-none"
            data-testid="input-check-in"
          />
        </div>
        <div>
          <Label>Check-out</Label>
          <input
            type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)}
            min={addDays(checkIn, 7)} max={addDays(checkIn, 21)}
            className="w-full h-11 px-3 rounded-xl border border-[#e0d6c2] text-sm focus:border-[#2f5d50] focus:outline-none"
            data-testid="input-check-out"
          />
        </div>
        <div>
          <Label>Guests</Label>
          <select
            value={guests} onChange={(e) => setGuests(Number(e.target.value))}
            className="w-full h-11 px-3 rounded-xl border border-[#e0d6c2] text-sm focus:border-[#2f5d50] focus:outline-none"
            data-testid="input-guests"
          >
            <option value={1}>1 guest</option>
            <option value={2}>2 guests</option>
          </select>
        </div>
      </div>

      <div className="rounded-xl p-3 bg-[#f3ece0]/60 border border-[#e0d6c2] text-sm flex items-center gap-3" data-testid="stay-summary">
        <Calendar className="w-4 h-4 text-[#2f5d50]" />
        <div>
          {validNights ? (
            <p><strong>{nights} nights</strong> in {selectedRoom?.name || 'a room'} · {guests} guest{guests > 1 ? 's' : ''}</p>
          ) : (
            <p className="text-[#a14b3a]">Stay must be between 7 and 21 nights (currently {nights})</p>
          )}
          {checking && <p className="text-xs text-[#6b6358] mt-0.5">Checking availability…</p>}
          {!checking && availability && validNights && (
            availability.available
              ? <p className="text-xs text-[#2f5d50] mt-0.5 font-semibold">✓ {availability.units_available} of {availability.total_units} {selectedRoom?.name}s available for these dates</p>
              : <p className="text-xs text-[#a14b3a] mt-0.5 font-semibold">No rooms of this category available. Try different dates or another room.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function StepTreatments({ treatments, treatmentIds, setTreatmentIds, nights }) {
  function toggle(id) {
    setTreatmentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }
  return (
    <div className="font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
      <h2 className="text-xl font-bold mb-1 text-[#2a2620]" style={{ fontFamily: 'Georgia, serif' }}>Add treatments (optional)</h2>
      <p className="text-xs text-[#6b6358] mb-5">Doctors finalise the plan after your arrival consultation. You can change selections later. Stay length: {nights} nights.</p>

      <div className="grid md:grid-cols-2 gap-3">
        {treatments.map((t) => {
          const active = treatmentIds.includes(t.id);
          const days = Math.min(nights, t.duration_days);
          const cost = days * t.price_per_day;
          return (
            <button
              key={t.id}
              onClick={() => toggle(t.id)}
              className={`text-left p-4 rounded-xl border-2 transition ${active ? 'border-[#2f5d50] bg-[#f3ece0]' : 'border-[#e0d6c2] bg-white hover:border-[#c89b3c]'}`}
              data-testid={`pick-treatment-${t.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-[#c89b3c] font-semibold">{t.category}</p>
                  <p className="text-sm font-bold mt-0.5">{t.name}</p>
                  <p className="text-[11px] text-[#6b6358] mt-1.5 line-clamp-2">{t.description}</p>
                </div>
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${active ? 'border-[#2f5d50] bg-[#2f5d50]' : 'border-[#c5b8a0]'}`}>
                  {active && <Check className="w-3 h-3 text-white" />}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[#e0d6c2] flex items-center justify-between text-xs">
                <span className="text-[#6b6358]">{days} days × {fmtUSD(t.price_per_day)}</span>
                <span className="font-bold text-[#2f5d50]">{fmtUSD(cost)}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepGuest({ guest, setGuest, consultation, setConsultation, checkIn, notes, setNotes }) {
  function set(k, v) { setGuest((g) => ({ ...g, [k]: v })); }
  return (
    <div className="font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
      <h2 className="text-xl font-bold mb-1 text-[#2a2620]" style={{ fontFamily: 'Georgia, serif' }}>Your details</h2>
      <p className="text-xs text-[#6b6358] mb-5">Shared only with our medical team to plan your treatment.</p>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name" required>
          <input value={guest.full_name} onChange={(e) => set('full_name', e.target.value)} className="i-input" data-testid="input-full-name" />
        </Field>
        <Field label="Email" required>
          <input type="email" value={guest.email} onChange={(e) => set('email', e.target.value)} className="i-input" data-testid="input-email" />
        </Field>
        <Field label="Phone (with country code)" required>
          <input value={guest.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+1 555 0100" className="i-input" data-testid="input-phone" />
        </Field>
        <Field label="Country" required>
          <input value={guest.country} onChange={(e) => set('country', e.target.value)} placeholder="USA" className="i-input" data-testid="input-country" />
        </Field>
        <Field label="Age" required>
          <input type="number" min="10" max="110" value={guest.age} onChange={(e) => set('age', e.target.value)} className="i-input" data-testid="input-age" />
        </Field>
        <Field label="Gender">
          <select value={guest.gender} onChange={(e) => set('gender', e.target.value)} className="i-input" data-testid="input-gender">
            <option value="">Prefer not to say</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </Field>
      </div>

      <h3 className="text-sm font-bold mt-7 mb-3 text-[#2f5d50]">Medical details (for your treatment team)</h3>
      <div className="grid gap-4">
        <Field label="Current ailments / why you're seeking treatment">
          <textarea rows="2" value={guest.current_ailments} onChange={(e) => set('current_ailments', e.target.value)} className="i-input" data-testid="input-ailments" />
        </Field>
        <Field label="Medical history (past conditions, surgeries)">
          <textarea rows="2" value={guest.medical_history} onChange={(e) => set('medical_history', e.target.value)} className="i-input" data-testid="input-medical-history" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Allergies">
            <input value={guest.allergies} onChange={(e) => set('allergies', e.target.value)} placeholder="None" className="i-input" data-testid="input-allergies" />
          </Field>
          <Field label="Current medications">
            <input value={guest.current_medications} onChange={(e) => set('current_medications', e.target.value)} placeholder="None" className="i-input" data-testid="input-medications" />
          </Field>
        </div>
      </div>

      <h3 className="text-sm font-bold mt-7 mb-3 text-[#2f5d50]">Pre-arrival doctor consultation (free)</h3>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox" checked={consultation.requested}
          onChange={(e) => setConsultation((c) => ({ ...c, requested: e.target.checked }))}
          className="mt-1 w-4 h-4 accent-[#2f5d50]"
          data-testid="input-consultation-req"
        />
        <div className="text-xs text-[#2a2620]">
          Yes, I&apos;d like a free 20-min video consultation with our chief physician before arrival
          to discuss my goals and customise the program.
        </div>
      </label>
      {consultation.requested && (
        <div className="grid sm:grid-cols-2 gap-4 mt-3 pl-7">
          <Field label="Preferred consultation date">
            <input type="date" value={consultation.preferred_date} min={today()} max={checkIn}
              onChange={(e) => setConsultation((c) => ({ ...c, preferred_date: e.target.value }))}
              className="i-input" data-testid="input-consult-date" />
          </Field>
          <Field label="Notes for the doctor">
            <input value={consultation.notes} onChange={(e) => setConsultation((c) => ({ ...c, notes: e.target.value }))} className="i-input" data-testid="input-consult-notes" />
          </Field>
        </div>
      )}

      <h3 className="text-sm font-bold mt-7 mb-3 text-[#2f5d50]">Anything else?</h3>
      <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Dietary preferences, mobility needs, etc." className="i-input" data-testid="input-notes" />

      <style>{`.i-input { width: 100%; min-height: 42px; padding: 8px 12px; border-radius: 12px; border: 1px solid #e0d6c2; background:#fff; font-size: 14px; color: #2a2620; outline:none; }
      .i-input:focus { border-color: #2f5d50; }`}</style>
    </div>
  );
}

function StepReview({ room, nights, checkIn, checkOut, guests, treatments, roomTotal, treatmentsTotal, grandTotal, guest, consultation, notes }) {
  return (
    <div className="font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
      <h2 className="text-xl font-bold mb-1 text-[#2a2620]" style={{ fontFamily: 'Georgia, serif' }}>Review & confirm</h2>
      <p className="text-xs text-[#6b6358] mb-5">Pay on arrival · We&apos;ll email confirmation immediately</p>

      <Section title="Your stay">
        {room && (
          <Row label={room.name}>
            <p className="text-xs text-[#6b6358]">{checkIn} → {checkOut} · {nights} nights · {guests} guest{guests > 1 ? 's' : ''}</p>
            <p className="text-sm font-bold text-[#2f5d50] mt-1">{fmtUSD(roomTotal)}</p>
          </Row>
        )}
      </Section>

      <Section title="Treatments">
        {treatments.length === 0 ? <p className="text-xs text-[#6b6358]">No treatments selected. You can add these on arrival.</p> :
          treatments.map((t) => (
            <Row key={t.id} label={t.name}>
              <p className="text-xs text-[#6b6358]">{Math.min(nights, t.duration_days)} days × {fmtUSD(t.price_per_day)}/day</p>
              <p className="text-sm font-bold text-[#2f5d50] mt-1">{fmtUSD(Math.min(nights, t.duration_days) * t.price_per_day)}</p>
            </Row>
          ))
        }
      </Section>

      <Section title="Guest details">
        <p className="text-sm"><UserIcon className="w-3.5 h-3.5 inline text-[#2f5d50]" /> {guest.full_name}, age {guest.age} · {guest.country}</p>
        <p className="text-xs text-[#6b6358] mt-1"><Mail className="w-3 h-3 inline" /> {guest.email}   ·   <Phone className="w-3 h-3 inline" /> {guest.phone}</p>
        {(guest.current_ailments || guest.medical_history) && (
          <p className="text-xs text-[#6b6358] mt-2 italic">
            {guest.current_ailments && <>Treating: {guest.current_ailments}. </>}
            {guest.medical_history && <>History: {guest.medical_history}.</>}
          </p>
        )}
        {consultation.requested && <p className="text-xs text-[#c89b3c] mt-2">📞 Pre-arrival consultation requested {consultation.preferred_date && `(${consultation.preferred_date})`}</p>}
        {notes && <p className="text-xs text-[#6b6358] mt-2">Notes: {notes}</p>}
      </Section>

      <div className="mt-4 p-4 rounded-xl bg-[#2f5d50] text-white flex items-center justify-between">
        <span className="text-sm font-semibold">Grand total (pay at arrival)</span>
        <span className="text-2xl font-bold">{fmtUSD(grandTotal)}</span>
      </div>
    </div>
  );
}

function SummarySidebar({ room, nights, checkIn, checkOut, treatments, roomTotal, treatmentsTotal, grandTotal, guests, availability }) {
  return (
    <aside className="bg-white rounded-2xl border border-[#e0d6c2] shadow-sm overflow-hidden sticky top-24 self-start font-sans" style={{ fontFamily: 'Inter, sans-serif' }} data-testid="booking-sidebar">
      {room && <img src={room.image_url} alt={room.name} className="w-full h-32 object-cover" />}
      <div className="p-5">
        <p className="text-[10px] uppercase tracking-widest text-[#c89b3c] font-semibold">Your booking</p>
        <h3 className="text-base font-bold mt-1">{room?.name || 'Select a room'}</h3>
        <div className="mt-3 text-xs text-[#6b6358] space-y-1">
          <p><Calendar className="w-3 h-3 inline" /> {checkIn} → {checkOut}</p>
          <p>{nights} nights · {guests} guest{guests > 1 ? 's' : ''}</p>
          {availability?.available && <p className="text-[#2f5d50] font-semibold">✓ Available</p>}
        </div>

        <div className="mt-4 pt-4 border-t border-[#e0d6c2] space-y-2 text-sm">
          {room && (
            <div className="flex justify-between">
              <span className="text-[#6b6358]">Room ({nights} nights)</span>
              <span>{fmtUSD(roomTotal)}</span>
            </div>
          )}
          {treatments.map((t) => (
            <div key={t.id} className="flex justify-between text-xs">
              <span className="text-[#6b6358]">{t.name}</span>
              <span>{fmtUSD(Math.min(nights, t.duration_days) * t.price_per_day)}</span>
            </div>
          ))}
          {treatments.length > 0 && (
            <div className="flex justify-between text-xs font-semibold pt-1 border-t border-[#e0d6c2]">
              <span>Treatments</span>
              <span>{fmtUSD(treatmentsTotal)}</span>
            </div>
          )}
          <div className="flex justify-between pt-3 mt-3 border-t border-[#e0d6c2] text-base font-bold text-[#2f5d50]">
            <span>Total</span>
            <span>{fmtUSD(grandTotal)}</span>
          </div>
          <p className="text-[10px] text-[#6b6358] italic">Pay at arrival in cash / card / international transfer.</p>
        </div>
      </div>
    </aside>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-4 pb-4 border-b border-[#e0d6c2] last:border-0">
      <p className="text-[10px] uppercase tracking-widest text-[#c89b3c] font-semibold mb-2">{title}</p>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="mb-2">
      <p className="text-sm font-bold">{label}</p>
      {children}
    </div>
  );
}

function Label({ children }) {
  return <label className="block text-[11px] uppercase tracking-widest text-[#6b6358] font-semibold mb-1.5">{children}</label>;
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label>{label}{required && <span className="text-[#a14b3a]"> *</span>}</Label>
      {children}
    </div>
  );
}
